import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GraduationCap, Plus, Pencil, Trash2, Eye, EyeOff, X, ExternalLink } from 'lucide-react';
import { supabase } from '../../services/adminService';

const TABLE = 'partner_training_links';

const emptyForm = {
  title: '',
  url: '',
  position: 0,
  is_active: true,
};

const withHttps = (raw) => {
  const s = String(raw || '').trim();
  if (!s) return '';
  if (/^https?:\/\//i.test(s)) return s;
  return `https://${s}`;
};

const isYoutubeUrl = (raw) => {
  const u = withHttps(raw);
  return /(?:youtube\.com\/watch\?|youtube\.com\/embed\/|youtube\.com\/shorts\/|youtu\.be\/)/i.test(u);
};

const PartnerTraining = () => {
  const queryClient = useQueryClient();
  const formRef = useRef(null);
  const [formData, setFormData] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const { data: items = [], isLoading, isError, error } = useQuery({
    queryKey: ['partnerTrainingLinks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(TABLE)
        .select('*')
        .order('position', { ascending: false })
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload) => {
      const { data, error } = await supabase.from(TABLE).insert([payload]).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partnerTrainingLinks']);
      setFormData(emptyForm);
      setEditingId(null);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }) => {
      const { data, error } = await supabase
        .from(TABLE)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['partnerTrainingLinks']);
      setFormData(emptyForm);
      setEditingId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase.from(TABLE).delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries(['partnerTrainingLinks']),
  });

  const handleSave = () => {
    const url = withHttps(formData.url);
    if (!url || !isYoutubeUrl(url)) {
      alert('Paste a YouTube URL only (youtube.com or youtu.be).');
      return;
    }
    const payload = {
      title: formData.title.trim() || null,
      url,
      position: Number(formData.position) || 0,
      is_active: formData.is_active !== false,
    };
    if (editingId) {
      updateMutation.mutate({ id: editingId, updates: payload });
      return;
    }
    createMutation.mutate({ ...payload, is_active: true });
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({
      title: item.title || '',
      url: item.url || '',
      position: item.position ?? 0,
      is_active: item.is_active !== false,
    });
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const sqlHint =
    error?.message ||
    createMutation.error?.message ||
    updateMutation.error?.message ||
    deleteMutation.error?.message ||
    '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Partner Training</h1>
        <p className="text-gray-600 mt-1">
          Add YouTube video URLs only. They play inside the partner app (Profile → Professional → Partner Training). Drive / other links are ignored.
        </p>
      </div>

      <div ref={formRef} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            {editingId ? <Pencil size={20} /> : <Plus size={20} />}
            {editingId ? 'Edit link' : 'Add training URL'}
          </h2>
          {editingId ? (
            <button
              type="button"
              onClick={() => {
                setEditingId(null);
                setFormData(emptyForm);
              }}
              className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
            >
              <X size={16} /> Cancel
            </button>
          ) : null}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            type="text"
            placeholder="Title (e.g. How to accept a job)"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <input
            type="url"
            placeholder="https://www.youtube.com/watch?v=... or youtu.be/..."
            value={formData.url}
            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 md:col-span-2"
          />
          <input
            type="number"
            placeholder="Priority (higher = first)"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={createMutation.isPending || updateMutation.isPending}
            className="px-5 py-2 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60"
          >
            {editingId ? 'Save' : 'Add URL'}
          </button>
        </div>
        {sqlHint ? (
          <p className="text-sm text-amber-700 font-medium">
            {sqlHint}. If the table is missing, run supabase/partner-training-links.sql in Supabase.
          </p>
        ) : null}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <GraduationCap size={18} className="text-primary-600" />
          <h2 className="font-semibold text-gray-900">Published links</h2>
          <span className="text-sm text-gray-500">{isLoading ? '…' : `${items.length}`}</span>
        </div>
        {isLoading ? (
          <p className="p-6 text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="p-6 text-gray-500">No training URLs yet. Add one above.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {items.map((item) => (
              <li key={item.id} className="px-6 py-4 flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900">{item.title || 'Partner Training'}</p>
                  <a
                    href={withHttps(item.url)}
                    target="_blank"
                    rel="noreferrer"
                    className="text-sm text-primary-600 hover:underline break-all inline-flex items-center gap-1"
                  >
                    {item.url} <ExternalLink size={12} />
                  </a>
                  {!item.is_active ? (
                    <p className="text-xs text-amber-700 mt-1 font-semibold">Hidden from partner app</p>
                  ) : null}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    title={item.is_active ? 'Hide' : 'Show'}
                    onClick={() =>
                      updateMutation.mutate({
                        id: item.id,
                        updates: { is_active: !item.is_active },
                      })
                    }
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    {item.is_active ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => handleEdit(item)}
                    className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => {
                      if (window.confirm('Delete this training link?')) deleteMutation.mutate(item.id);
                    }}
                    className="p-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default PartnerTraining;
